import React from "react";
import { Box, CircularProgress } from "@mui/joy";
import { SCreate } from "../../../styles/StyledMUI";
import { textTokens } from "../../../styles/globalStyles";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { usePermissions, Permission } from "../../../contexts/app/permissions";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import { useTraining } from "../../../contexts/api";
import usePrestations from "./hooks/usePrestations";
import PrestationFormModal from "./display/PrestationFormModal";
import PrestationGroupCard from "./display/PrestationGroupCard";
import GroupFilter from "../shared/GroupFilter";
import {
  GROUP_FILTER_ALL,
  groupByGroupName,
  extractGroupOptions,
  filterByGroup,
} from "../shared/groupUtils";
import type { Prestation, PrestationRequest } from "../../../api/billing/Billing.typed";

export default function PrestationsPage() {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.BILLING_CATALOGUE_EDIT);
  const { prestations, loading, create, update, remove } = usePrestations();
  const { qualifications, refreshTrainingTemplates } = useTraining();
  const { showConfirmation } = useConfirmationModal();

  React.useEffect(() => {
    if (!qualifications) refreshTrainingTemplates();
  }, [qualifications, refreshTrainingTemplates]);

  const qualifMap = React.useMemo(() => {
    const m = new Map<number, string>();
    qualifications?.forEach((q) => m.set(q.ID, `${q.Agency} – ${q.Qualification}`));
    return m;
  }, [qualifications]);

  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Prestation | null>(null);
  const [selectedGroup, setSelectedGroup] = React.useState(GROUP_FILTER_ALL);

  const groupOptions = React.useMemo(() => extractGroupOptions(prestations), [prestations]);
  const filtered = React.useMemo(
    () => filterByGroup(prestations, selectedGroup),
    [prestations, selectedGroup],
  );
  const groupedRows = React.useMemo(() => groupByGroupName(filtered), [filtered]);

  const handleCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const handleEdit = (p: Prestation) => {
    setEditing(p);
    setModalOpen(true);
  };
  const handleSubmit = async (req: PrestationRequest) => {
    if (editing) await update(editing.ID, req);
    else await create(req);
  };
  const handleDelete = async (p: Prestation) => {
    const ok = await showConfirmation("Supprimer la prestation", `Supprimer « ${p.Name} » ?`);
    if (ok) await remove(p.ID);
  };

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2 },
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <DarkTypography level="h4">Prestations</DarkTypography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { sm: "flex-end" },
            flex: { sm: "1 1 auto" },
            minWidth: 0,
            justifyContent: "flex-end",
          }}
        >
          <GroupFilter value={selectedGroup} onChange={setSelectedGroup} options={groupOptions} />
          {canEdit && (
            <IconButtonCTA aria-label="Créer une prestation" onClick={handleCreate}>
              <SCreate />
            </IconButtonCTA>
          )}
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size="sm" />
        </Box>
      )}

      {!loading && prestations.length === 0 && (
        <DarkTypography level="body-md" sx={{ color: textTokens.color.dimmed, mt: 2 }}>
          Aucune prestation.
        </DarkTypography>
      )}

      {!loading && prestations.length > 0 && filtered.length === 0 && (
        <DarkTypography level="body-md" sx={{ color: textTokens.color.dimmed, mt: 2 }}>
          Aucune prestation ne correspond au filtre sélectionné.
        </DarkTypography>
      )}

      {groupedRows.map((group) => (
        <PrestationGroupCard
          key={group.groupName || "__ungrouped"}
          groupName={group.groupName}
          items={group.items}
          qualifMap={qualifMap}
          canEdit={canEdit}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ))}

      <PrestationFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        initial={editing}
      />
    </Box>
  );
}
